import React, { Fragment, useEffect, useState } from 'react';
import { getStoredUser } from '../utils/auth';

const actionButtonClass =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed lg:h-8 lg:w-8';
const actionIconClass = 'h-[26px] w-[26px]';

function ActionIconButton({ onClick, disabled, title, active, tone, children }) {
  const toneClass =
    tone === 'notes'
      ? active
        ? 'bg-emerald-400/10 border-emerald-300/40'
        : 'border-emerald-300/30 hover:bg-emerald-300/10'
      : tone === 'edit'
        ? 'text-amber-300 border-amber-300/30 hover:bg-amber-300/10 hover:text-amber-200'
        : tone === 'delete'
          ? 'text-red-400 border-red-400/30 hover:bg-red-400/10 hover:text-red-300'
          : tone === 'add'
            ? 'text-amber-300 border-gray-600 hover:border-amber-300/50 hover:bg-amber-300/10 hover:text-amber-200'
            : tone === 'remove'
              ? 'text-gray-300 border-gray-600 hover:border-red-400/50 hover:text-red-400 hover:bg-red-400/10'
              : tone === 'info'
                ? 'text-sky-300 border-sky-300/30 hover:bg-sky-300/10 hover:text-sky-200'
                : tone === 'certificate'
                  ? 'text-violet-300 border-violet-300/30 hover:bg-violet-300/10 hover:text-violet-200'
                : 'text-sky-300 border-sky-300/30 hover:bg-sky-300/10 hover:text-sky-200';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`${actionButtonClass} ${toneClass}`}
    >
      {children}
    </button>
  );
}

export function NotesIcon() {
  return (
    <svg viewBox="0 0 24 24" className={actionIconClass} aria-hidden>
      <rect x="4.5" y="3.5" width="13" height="17" rx="1.75" fill="#ffffff" stroke="#cbd5e1" strokeWidth="0.6" />
      <path
        d="M4.5 5.25C4.5 4.28 5.28 3.5 6.25 3.5H15.75C16.72 3.5 17.5 4.28 17.5 5.25V7.5H4.5V5.25Z"
        fill="#3b82f6"
      />
      <line x1="7" y1="10.25" x2="15" y2="10.25" stroke="#cbd5e1" strokeWidth="0.85" strokeLinecap="round" />
      <line x1="7" y1="12.75" x2="15" y2="12.75" stroke="#cbd5e1" strokeWidth="0.85" strokeLinecap="round" />
      <line x1="7" y1="15.25" x2="13.5" y2="15.25" stroke="#cbd5e1" strokeWidth="0.85" strokeLinecap="round" />
      <g transform="translate(12.5 11.5) rotate(-38)">
        <rect x="-1.15" y="-7.5" width="2.3" height="3.1" rx="0.45" fill="#f472b6" />
        <rect x="-1.15" y="-4.55" width="2.3" height="1.35" fill="#94a3b8" />
        <rect x="-1.15" y="-3.35" width="2.3" height="9.2" rx="0.35" fill="#fbbf24" />
        <path d="M-1.15 5.85 L1.15 5.85 L0 8.35 Z" fill="#fde68a" />
        <path d="M-0.45 7.15 L0.45 7.15 L0 8.35 Z" fill="#374151" />
      </g>
    </svg>
  );
}

export function EditIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={actionIconClass} aria-hidden>
      <path d="M12.5 4.5 15.5 7.5 7 16H4v-3L12.5 4.5Z" strokeLinejoin="round" />
      <path d="M11 6 14 9" strokeLinecap="round" />
    </svg>
  );
}

export function DeleteIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={actionIconClass} aria-hidden>
      <path d="M4.5 6.5h11" strokeLinecap="round" />
      <path d="M8 6.5V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.5 6.5 7.2 15a1.5 1.5 0 0 0 1.5 1.3h3.6a1.5 1.5 0 0 0 1.5-1.3l.7-8.5" strokeLinejoin="round" />
    </svg>
  );
}

export function ResetPasswordIcon({ className = actionIconClass }) {
  const gradId = React.useId().replace(/:/g, '');

  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden>
      <defs>
        <linearGradient id={gradId} x1="5" y1="3.5" x2="19.5" y2="20.5" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#7c3aed" />
        </linearGradient>
      </defs>
      <path
        d="M12 4.1a7.9 7.9 0 0 1 7.35 5"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.45"
        strokeLinecap="round"
        strokeDasharray="2.3 2"
      />
      <path
        d="M18.75 8.35 20.1 9.15 18.55 9.95"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.45"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.1 12.35a7.9 7.9 0 0 1-13.85 4.15"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.45"
        strokeLinecap="round"
        strokeDasharray="2.3 2"
      />
      <path
        d="M5.85 15.85 4.55 16.85 5.15 15.05"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.45"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.85 11.35V9.55a3.15 3.15 0 0 1 6.3 0v1.8"
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect x="7.65" y="11.35" width="8.7" height="6.35" rx="1.25" fill={`url(#${gradId})`} />
      <circle cx="12" cy="13.95" r="0.8" fill="#ffffff" />
      <path d="M12 14.35v1.35" stroke="#ffffff" strokeWidth="1.05" strokeLinecap="round" />
      <rect x="13.15" y="14.75" width="6.55" height="3.35" rx="0.95" fill={`url(#${gradId})`} />
      <path d="M14.35 18.1h0.65v0.55h-0.65z" fill={`url(#${gradId})`} />
      <text
        x="13.55"
        y="17.05"
        fill="#ffffff"
        fontSize="2.45"
        fontFamily="system-ui, sans-serif"
        fontWeight="700"
      >
        ***
      </text>
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={actionIconClass} aria-hidden>
      <path d="M10 5v10M5 10h10" strokeLinecap="round" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={actionIconClass} aria-hidden>
      <path d="M5 10h10" strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className={actionIconClass} aria-hidden>
      <circle cx="10" cy="10" r="7" />
      <path d="M10 9v5" strokeLinecap="round" />
      <circle cx="10" cy="6.25" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function SiteInfoButton({ onClick }) {
  return (
    <ActionIconButton onClick={onClick} title="Site info" tone="info">
      <InfoIcon />
    </ActionIconButton>
  );
}

export function EditButton({ onClick }) {
  return (
    <ActionIconButton onClick={onClick} title="Edit" tone="edit">
      <EditIcon />
    </ActionIconButton>
  );
}

function CertificateIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" className={actionIconClass} aria-hidden>
      <rect x="3.5" y="3.5" width="13" height="10" rx="1.4" />
      <path d="M7 7h6M7 9.5h4" strokeLinecap="round" />
      <circle cx="10" cy="14.4" r="2.3" />
      <path d="M8.7 16.1 8.2 18.4 10 17.4 11.8 18.4 11.3 16.1" strokeLinejoin="round" />
    </svg>
  );
}

export function CertificateButton({ onClick, active }) {
  return (
    <ActionIconButton onClick={onClick} title="Certificate" tone="certificate" active={active}>
      <CertificateIcon />
    </ActionIconButton>
  );
}

export function DeleteButton({ onClick, disabled, deleting, title = 'Delete' }) {
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!confirming) return undefined;
    const timer = window.setTimeout(() => setConfirming(false), 3000);
    return () => window.clearTimeout(timer);
  }, [confirming]);

  useEffect(() => {
    if (deleting) setConfirming(false);
  }, [deleting]);

  const handleClick = () => {
    if (disabled || deleting) return;
    if (confirming) {
      setConfirming(false);
      onClick?.();
      return;
    }
    setConfirming(true);
  };

  if (confirming) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || deleting}
        title="Click again to confirm"
        aria-label="Confirm delete"
        className="inline-flex h-11 min-w-[3.25rem] shrink-0 items-center justify-center rounded border border-red-700 bg-red-700 px-2 text-xs font-semibold text-red-50 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed lg:h-8 lg:px-1.5"
      >
        Sure?
      </button>
    );
  }

  return (
    <ActionIconButton
      onClick={handleClick}
      disabled={disabled || deleting}
      title={deleting ? 'Deleting...' : title}
      tone="delete"
    >
      <DeleteIcon />
    </ActionIconButton>
  );
}

export function RestoreButton({ onClick, disabled, restoring, title = 'Restore' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || restoring}
      title={restoring ? 'Restoring...' : title}
      className="inline-flex items-center min-h-11 px-3 rounded border border-emerald-400/40 text-xs font-semibold text-emerald-300 hover:bg-emerald-400/10 disabled:opacity-50 disabled:cursor-not-allowed lg:min-h-8 lg:px-2.5"
    >
      {restoring ? 'Restoring…' : 'Restore'}
    </button>
  );
}

export function ResetPasswordButton({ onClick }) {
  return (
    <ActionIconButton onClick={onClick} title="Reset password" tone="reset">
      <ResetPasswordIcon />
    </ActionIconButton>
  );
}

export function NotesButton({ onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={active ? 'Hide notes' : 'Open notes'}
      aria-label={active ? 'Hide notes' : 'Open notes'}
      aria-expanded={active}
      className={`inline-flex h-11 shrink-0 items-center gap-0.5 rounded border px-2 transition-colors lg:h-8 lg:px-1.5 ${
        active
          ? 'bg-emerald-400/10 border-emerald-300/40'
          : 'border-emerald-300/30 hover:bg-emerald-300/10'
      }`}
    >
      <NotesIcon />
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        className={`h-3.5 w-3.5 text-emerald-300 transition-transform ${active ? 'rotate-180' : ''}`}
        aria-hidden
      >
        <path d="M5 7.5 10 12.5 15 7.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

export function AddFieldButton({ onClick, title = 'Add another note', disabled = false }) {
  return (
    <ActionIconButton onClick={onClick} title={title} tone="add" disabled={disabled}>
      <PlusIcon />
    </ActionIconButton>
  );
}

export function RemoveFieldButton({ onClick, title = 'Remove this field' }) {
  return (
    <ActionIconButton onClick={onClick} title={title} tone="remove">
      <MinusIcon />
    </ActionIconButton>
  );
}

export function AdminDataTable({
  columns,
  rows,
  emptyText,
  onDelete,
  onEdit,
  onCertificate,
  onResetPassword,
  onNotes,
  onRestore,
  notesExpandedId,
  renderNotesRow,
  deletingId,
  restoringId,
  canDeleteRow,
  canEditRow,
  canResetPasswordRow,
}) {
  const rowDeletable = canDeleteRow || (() => true);
  const rowEditable = canEditRow || (() => true);
  const rowResettable = canResetPasswordRow || canEditRow || (() => true);
  const hasActions = onDelete || onEdit || onCertificate || onResetPassword || onNotes || onRestore;
  const colSpan = columns.length + (hasActions ? 1 : 0);

  if (!rows?.length) {
    return (
      <p className="text-gray-500 text-sm py-6 text-center border border-gray-800 rounded-lg">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto overscroll-x-contain border border-gray-800 rounded-lg">
      <table className="w-full min-w-[44rem] text-sm text-left lg:min-w-0">
        <thead className="bg-gray-900 text-gray-400 uppercase text-xs">
          <tr>
            {columns.map((col) => (
              <th key={col.key} className="px-4 py-3 whitespace-nowrap">
                {col.label}
              </th>
            ))}
            {hasActions && (
              <th className="sticky right-0 z-20 px-4 py-3 whitespace-nowrap text-right w-[1%] bg-gray-900 lg:static">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <Fragment key={row.id ?? idx}>
              <tr className="border-t border-gray-800 hover:bg-gray-900/50">
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-4 py-3 text-gray-300 max-w-[14rem] break-words [overflow-wrap:anywhere] lg:max-w-none lg:break-normal"
                  >
                    {col.render ? col.render(row) : row[col.key] ?? '—'}
                  </td>
                ))}
                {hasActions && (
                  <td className="sticky right-0 z-10 px-4 py-3 text-right whitespace-nowrap bg-black lg:static">
                    <div className="inline-flex items-center justify-end gap-1.5 flex-nowrap">
                      {onNotes && (
                        <NotesButton
                          active={notesExpandedId === row.id}
                          onClick={() => onNotes(row)}
                        />
                      )}
                      {onEdit && rowEditable(row) && (
                        <EditButton onClick={() => onEdit(row)} />
                      )}
                      {onCertificate && rowEditable(row) && (
                        <CertificateButton onClick={() => onCertificate(row)} />
                      )}
                      {onResetPassword && rowResettable(row) && (
                        <ResetPasswordButton onClick={() => onResetPassword(row)} />
                      )}
                      {onRestore && (
                        <RestoreButton
                          onClick={() => onRestore(row)}
                          restoring={restoringId === row.id}
                        />
                      )}
                      {onDelete && (
                        rowDeletable(row) ? (
                          <DeleteButton
                            onClick={() => onDelete(row)}
                            deleting={deletingId === row.id}
                          />
                        ) : (
                          <span
                            className="text-xs text-gray-600"
                            title={
                              row.email === getStoredUser()?.email
                                ? 'Cannot delete your own account'
                                : 'Cannot delete this account'
                            }
                          >
                            —
                          </span>
                        )
                      )}
                    </div>
                  </td>
                )}
              </tr>
              {notesExpandedId === row.id && renderNotesRow && (
                <tr className="border-t border-gray-800">
                  <td colSpan={colSpan} className="p-0">
                    {renderNotesRow(row)}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
